/**
 * Shopify Commissionable Link Tracking Script
 * 
 * This script should be injected into Shopify pages to:
 * 1. Capture UTM parameters from URLs
 * 2. Store them in session/localStorage
 * 3. Pass them to order data when checkout occurs
 * 4. Track commissionable link clicks
 * 
 * Usage: Add this script to your Shopify theme's liquid templates
 */

// Capture attribution params before anything else
(function() {
    // 1) Read params
    const u = new URLSearchParams(location.search);
    const params = {
        utm_source: u.get('utm_source') || '',
        utm_medium: u.get('utm_medium') || '',
        utm_campaign: u.get('utm_campaign') || '',
        utm_term: u.get('utm_term') || '',
        utm_content: u.get('utm_content') || '',
        creator: u.get('creator') || '',
        promo: u.get('promo') || u.get('discount') || ''
    };

    // 2) Load existing (first-touch) if present
    const k = 'steer_attrib';
    const existing = JSON.parse(localStorage.getItem(k) || '{}');
    if (!existing.firstTouchAt) {
        params.firstTouchAt = new Date().toISOString();
        localStorage.setItem(k, JSON.stringify(params));
    }

    // 3) Also set a cookie for server joins
    document.cookie = `steer_attrib=${encodeURIComponent(localStorage.getItem(k) || JSON.stringify(params))};path=/;max-age=${60*60*24*30}`;
    
    // 4) Store in window for access by tracking script
    window.steerAttribution = JSON.parse(localStorage.getItem(k) || JSON.stringify(params));
})();

(function() {
    'use strict';
    
    // Configuration
    const CONFIG = {
        API_BASE_URL: window.SteerConfig?.apiBaseUrl || 'https://dev.usesteer.io', // Default to dev, override with SteerConfig
        SESSION_STORAGE_KEY: 'steer_commissionable_tracking',
        COOKIE_EXPIRY_DAYS: 30,
        DEBUG: window.SteerConfig?.debug || false // Set via SteerConfig
    };
    
    // Utility functions
    const utils = {
        log: function(message, data = null) {
            if (CONFIG.DEBUG) {
                console.log('[Steer Tracking]', message, data);
            }
        },
        
        error: function(message, error = null) {
            console.error('[Steer Tracking Error]', message, error);
        },
        
        getUrlParams: function() {
            const params = new URLSearchParams(window.location.search);
            const utmParams = {};
            
            // Extract UTM parameters
            ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
                if (params.has(param)) {
                    utmParams[param] = params.get(param);
                }
            });
            
            return utmParams;
        },
        
        getCookie: function(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        },
        
        setCookie: function(name, value, days) {
            const expires = new Date(Date.now() + days * 864e5).toUTCString();
            document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax; domain=.glowbar.com`;
            document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
        },
        
        ensureSessionId: function() {
            let sid = this.getCookie('steer_sid');
            if (!sid) {
                try {
                    sid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : ('session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
                } catch (e) {
                    sid = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                }
                this.setCookie('steer_sid', sid, CONFIG.COOKIE_EXPIRY_DAYS);
            }
            return sid;
        },
        
        addParamsToUrl: function(url, params) {
            try {
                const u = new URL(url, window.location.origin);
                Object.entries(params || {}).forEach(([key, value]) => {
                    if (value == null || value === '') return;
                    if (!u.searchParams.has(key)) u.searchParams.set(key, value);
                });
                return u.toString();
            } catch (e) {
                return url;
            }
        },
        
        getSessionData: function() {
            try {
                const data = sessionStorage.getItem(CONFIG.SESSION_STORAGE_KEY);
                return data ? JSON.parse(data) : {};
            } catch (e) {
                return {};
            }
        },
        
        setSessionData: function(data) {
            try {
                sessionStorage.setItem(CONFIG.SESSION_STORAGE_KEY, JSON.stringify(data));
            } catch (e) {
                utils.error('Failed to save session data', e);
            }
        },
        
        isCommissionableLink: function(utmParams) {
            // Check if this is a Steer commissionable link
            // Only track if utm_source looks like a social handle (contains underscore or @)
            const isSteerLink = utmParams.utm_source && utmParams.utm_campaign && 
                               (utmParams.utm_source.includes('_') || utmParams.utm_source.includes('@'));
            utils.log('Checking if Steer commissionable link:', { utmParams, isSteerLink });
            return isSteerLink;
        }
    };
    
    // Main tracking object
    const SteerTracking = {
        
        init: function() {
            utils.log('Initializing Steer tracking');
            const steerSid = utils.ensureSessionId();
            utils.log('Steer session id', steerSid);
            
            // Capture UTM parameters from URL
            this.captureUtmParams();
            
            // Track page views
            this.trackPageView();
            
            // Set up checkout tracking
            this.setupCheckoutTracking();
            
            // Set up link click tracking
            this.setupLinkTracking();
            
            // Decorate booking entrypoints
            this.decorateBookingEntrypoints();
            
            // Observe for booking widgets
            this.observeForBookingWidgets();
        },
        
        captureUtmParams: function() {
            const utmParams = utils.getUrlParams();
            
            if (Object.keys(utmParams).length > 0) {
                utils.log('UTM parameters found', utmParams);
                
                // Store in session storage
                const sessionData = utils.getSessionData();
                sessionData.utmParams = utmParams;
                sessionData.timestamp = Date.now();
                utils.setSessionData(sessionData);
                
                // Also store in cookie for persistence across sessions
                utils.setCookie('steer_utm_params', JSON.stringify(utmParams), CONFIG.COOKIE_EXPIRY_DAYS);
                
                // Track commissionable link visit
                if (utils.isCommissionableLink(utmParams)) {
                    utils.log('Commissionable link detected, tracking visit');
                    this.trackCommissionableLinkVisit(utmParams);
                } else {
                    utils.log('Not a commissionable link, UTM params:', utmParams);
                }
            } else {
                utils.log('No UTM parameters found in URL');
            }
        },
        
        trackPageView: function() {
            const sessionData = utils.getSessionData();
            const pageData = {
                url: window.location.href,
                title: document.title,
                timestamp: Date.now(),
                referrer: document.referrer
            };
            
            // Add to page views array
            if (!sessionData.pageViews) {
                sessionData.pageViews = [];
            }
            sessionData.pageViews.push(pageData);
            
            // Keep only last 10 page views
            if (sessionData.pageViews.length > 10) {
                sessionData.pageViews = sessionData.pageViews.slice(-10);
            }
            
            utils.setSessionData(sessionData);
            utils.log('Page view tracked', pageData);
        },
        
        getCarryParams: function() {
            const sessionData = utils.getSessionData();
            const utmParams = (sessionData && sessionData.utmParams) ? sessionData.utmParams : {};
            const steerSid = utils.ensureSessionId();
            return Object.assign({ steer_sid: steerSid }, utmParams);
        },
        
        decorateBookingEntrypoints: function() {
            const carry = this.getCarryParams();
            const candidates = document.querySelectorAll('a[href], button');
            candidates.forEach((el) => {
                const href = el.getAttribute('href') || '';
                const text = (el.textContent || el.value || '').toLowerCase();
                const isBook = /book|booking|appointment|schedule/.test((href + ' ' + text).toLowerCase()) || href === '#book-now';
                if (!isBook) return;
                if (href && /^https?:/i.test(href)) {
                    try { el.setAttribute('href', utils.addParamsToUrl(href, carry)); } catch (e) {}
                }
            });
            const ifr = document.querySelector('#web-booking-widget, .web-booking-widget, iframe[src*="booking.glowbar.com"]');
            if (ifr) {
                const src = ifr.getAttribute('src') || '';
                if (src && !/steer_sid=/.test(src)) {
                    ifr.setAttribute('src', utils.addParamsToUrl(src, carry));
                }
            }
        },
        
        observeForBookingWidgets: function() {
            const observer = new MutationObserver(() => {
                this.decorateBookingEntrypoints();
            });
            observer.observe(document.documentElement, { childList: true, subtree: true });
        },
        
        trackCommissionableLinkVisit: function(utmParams) {
            const steerSid = utils.ensureSessionId();
            
            // Send tracking data to SessionTracking API
            fetch(`${CONFIG.API_BASE_URL}/api/shopify/track-visit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    utmParams: utmParams,
                    pageUrl: window.location.href,
                    timestamp: Date.now(),
                    userAgent: navigator.userAgent,
                    steer_sid: steerSid
                })
            }).then(response => {
                if (response.ok) {
                    utils.log('Commissionable link visit tracked successfully');
                } else {
                    utils.error('Failed to track commissionable link visit', response.statusText);
                }
            }).catch(error => {
                utils.error('Failed to track commissionable link visit', error);
            });
        },
        
        setupCheckoutTracking: function() {
            // Override Shopify's checkout form to include UTM parameters
            const originalFetch = window.fetch;
            window.fetch = function(url, options) {
                // Check if this is a Shopify checkout request
                if (url.includes('/cart/add') || url.includes('/checkout') || url.includes('/cart/update')) {
                    const sessionData = utils.getSessionData();
                    
                    if (sessionData.utmParams && Object.keys(sessionData.utmParams).length > 0) {
                        utils.log('Adding UTM parameters to checkout request');
                        
                        // Add UTM parameters to form data
                        if (options && options.body) {
                            if (options.body instanceof FormData) {
                                // Add UTM params to FormData
                                Object.entries(sessionData.utmParams).forEach(([key, value]) => {
                                    options.body.append(key, value);
                                });
                                
                                // Add session tracking data
                                options.body.append('steer_session_id', this.generateSessionId());
                                options.body.append('steer_tracking_data', JSON.stringify(sessionData));
                            }
                        }
                    }
                }
                
                return originalFetch.call(this, url, options);
            };
            
            // Also hook into Shopify's native cart events
            document.addEventListener('DOMContentLoaded', function() {
                // Override add to cart buttons
                document.addEventListener('click', function(e) {
                    if (e.target.matches('[data-add-to-cart]') || 
                        e.target.closest('[data-add-to-cart]') ||
                        e.target.matches('.btn-cart') ||
                        e.target.closest('.btn-cart')) {
                        
                        const sessionData = utils.getSessionData();
                        if (sessionData.utmParams) {
                            utils.log('Add to cart clicked with UTM params', sessionData.utmParams);
                            
                            // Store UTM params for checkout
                            utils.setCookie('steer_checkout_utm', JSON.stringify(sessionData.utmParams), 1);
                        }
                    }
                });
            });
        },
        
        setupLinkTracking: function() {
            // Track clicks on commissionable links
            document.addEventListener('click', function(e) {
                const link = e.target.closest('a');
                if (!link) return;
                
                const href = link.href;
                // Extract UTM params from the clicked link's URL (not the current page URL)
                let utmParams = {};
                try {
                    const urlObj = new URL(href, window.location.origin);
                    const searchParams = urlObj.searchParams;
                    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
                        if (searchParams.has(param)) {
                            utmParams[param] = searchParams.get(param);
                        }
                    });
                } catch (err) {
                    // Fallback to current page params if URL parsing fails
                    utmParams = utils.getUrlParams();
                }
                
                // Check if this is a commissionable link
                if (href && (href.includes('utm_') || href.includes('steer'))) {
                    utils.log('Commissionable link clicked', { href, utmParams });
                    
                    // Track the click
                    SteerTracking.trackLinkClick(href, utmParams);
                }
            });
        },
        
        trackLinkClick: function(href, utmParams) {
            const steerSid = utils.ensureSessionId();
            
            fetch(`${CONFIG.API_BASE_URL}/api/shopify/track-click`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: href,
                    utmParams: utmParams,
                    timestamp: Date.now(),
                    pageUrl: window.location.href,
                    steer_sid: steerSid
                })
            }).catch(error => {
                utils.error('Failed to track link click', error);
            });
        },
        
        generateSessionId: function() {
            return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        },
        
        getTrackingData: function() {
            return utils.getSessionData();
        },
        
        associateCustomer: function(customerData) {
            /**
             * Associate customer with existing session tracking when they login/create account
             */
            const sessionData = utils.getSessionData();
            
            utils.log('=== Customer Association Debug ===');
            utils.log('Customer data:', customerData);
            utils.log('Session data:', sessionData);
            utils.log('UTM params:', sessionData.utmParams);
            
            if (!sessionData.utmParams || Object.keys(sessionData.utmParams).length === 0) {
                utils.log('No UTM parameters to associate with customer - but proceeding anyway for testing');
                // Don't return early, continue with the request
            }
            
            const steerSid = utils.ensureSessionId();
            
            const payload = {
                email: customerData.email,
                first_name: customerData.first_name,
                last_name: customerData.last_name,
                phone_number: customerData.phone_number,
                session_id: steerSid,
                utmParams: sessionData.utmParams,
                pageUrl: window.location.href,
                timestamp: Date.now()
            };
            
            return fetch(`${CONFIG.API_BASE_URL}/api/shopify/associate-customer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            }).then(response => {
                if (response.ok) {
                    utils.log('Customer associated with session tracking successfully');
                    return response.json();
                } else {
                    utils.error('Failed to associate customer with session tracking', response.statusText);
                    throw new Error('Failed to associate customer');
                }
            }).catch(error => {
                utils.error('Failed to associate customer with session tracking', error);
                throw error;
            });
        },
        
        setupCustomerAssociation: function() {
            /**
             * Set up customer association for multi-step booking forms
             */
            utils.log('Setting up customer association tracking');
            
            // Track customer data as it's being filled out
            this.trackCustomerDataInput();
            
            // Also track form submissions
            this.trackFormSubmissions();
            
            // Listen for cross-domain messages from booking subdomain
            this.setupCrossDomainTracking();
            
            // Setup custom Boulevard widget tracking
            this.setupCustomWidgetTracking();
        },
        
        setupCrossDomainTracking: function() {
            /**
             * Listen for customer data from booking subdomain
             */
            window.addEventListener('message', (event) => {
                // Only accept messages from booking.glowbar.com
                if (event.origin !== 'https://booking.glowbar.com') {
                    return;
                }
                
                if (event.data.type === 'CUSTOMER_DATA') {
                    utils.log('Received customer data from booking subdomain:', event.data.customerData);
                    this.associateCustomer(event.data.customerData);
                }
            });
        },
        
        setupCustomWidgetTracking: function() {
            /**
             * Setup tracking for custom Boulevard widget
             */
            utils.log('Setting up custom Boulevard widget tracking');
            
            // Listen for custom widget events via postMessage
            window.addEventListener('message', (event) => {
                // Only accept messages from Boulevard domains
                if (event.origin.includes('boulevard') || event.origin.includes('glowbar')) {
                    utils.log('Custom Boulevard widget message received:', event.data);
                    
                    if (event.data && event.data.type === 'BOULEVARD_BOOKING') {
                        utils.log('Custom Boulevard booking started');
                        this.trackCustomWidgetEvent('booking_started', event.data);
                    } else if (event.data && event.data.type === 'BOULEVARD_FORM_SUBMIT') {
                        utils.log('Custom Boulevard form submitted');
                        this.trackCustomWidgetEvent('form_submitted', event.data);
                    }
                }
            });
            
            // Listen for form submissions in the widget
            document.addEventListener('submit', (e) => {
                // Check if this is a Boulevard widget form
                const isBoulevardForm = e.target.closest('[data-boulevard-widget]') || 
                                      e.target.closest('.boulevard-widget') ||
                                      e.target.action?.includes('boulevard') ||
                                      e.target.action?.includes('glowbar');
                
                if (isBoulevardForm) {
                    utils.log('Custom Boulevard form submitted via submit event');
                    this.trackCustomWidgetEvent('form_submitted', {
                        form: e.target,
                        formData: new FormData(e.target)
                    });
                }
            });
            
            // Watch for custom widget DOM changes
            this.setupCustomWidgetObserver();
        },
        
        setupCustomWidgetObserver: function() {
            /**
             * Use MutationObserver to watch for custom widget DOM changes
             */
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        // Check if Boulevard widget was added
                        const boulevardForms = document.querySelectorAll(
                            '[data-boulevard], .boulevard-widget, [id*="boulevard"], [class*="boulevard"]'
                        );
                        
                        if (boulevardForms.length > 0) {
                            utils.log('Custom Boulevard widget detected in DOM');
                            this.setupCustomWidgetFormTracking(boulevardForms);
                        }
                    }
                });
            });
            
            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        },
        
        setupCustomWidgetFormTracking: function(forms) {
            /**
             * Setup tracking for custom widget forms
             */
            forms.forEach((form) => {
                // Track input changes
                form.addEventListener('input', (e) => {
                    const field = e.target;
                    const fieldName = field.name || field.id || field.placeholder || '';
                    const fieldValue = field.value;
                    
                    if (fieldValue && this.isCustomerDataField(field, fieldName)) {
                        utils.log('Custom Boulevard widget field changed:', fieldName, fieldValue);
                        this.trackCustomWidgetFieldChange(fieldName, fieldValue);
                    }
                });
                
                // Track form submission
                form.addEventListener('submit', (e) => {
                    utils.log('Custom Boulevard widget form submitted');
                    this.trackCustomWidgetEvent('form_submitted', {
                        form: e.target,
                        formData: new FormData(e.target)
                    });
                });
            });
        },
        
        trackCustomWidgetEvent: function(eventType, data) {
            /**
             * Track custom widget events
             */
            utils.log(`Custom Boulevard widget event: ${eventType}`, data);
            
            // Send to Google Analytics if available
            if (typeof gtag !== 'undefined') {
                gtag('event', eventType, {
                    event_category: 'Boulevard Widget',
                    event_label: 'Custom Widget',
                    custom_parameters: data
                });
            }
            
            // Store in session data for later association
            const sessionData = utils.getSessionData();
            if (!sessionData.customWidgetEvents) {
                sessionData.customWidgetEvents = [];
            }
            sessionData.customWidgetEvents.push({
                type: eventType,
                data: data,
                timestamp: Date.now()
            });
            utils.setSessionData(sessionData);
        },
        
        trackCustomWidgetFieldChange: function(fieldName, fieldValue) {
            /**
             * Track custom widget field changes
             */
            const sessionData = utils.getSessionData();
            if (!sessionData.customWidgetData) {
                sessionData.customWidgetData = {};
            }
            
            // Map field names to customer data
            const name = fieldName.toLowerCase();
            if (name.includes('first') || name.includes('given')) {
                sessionData.customWidgetData.first_name = fieldValue;
            } else if (name.includes('last') || name.includes('family') || name.includes('surname')) {
                sessionData.customWidgetData.last_name = fieldValue;
            } else if (name.includes('email')) {
                sessionData.customWidgetData.email = fieldValue;
            } else if (name.includes('phone') || name.includes('mobile')) {
                sessionData.customWidgetData.phone_number = fieldValue;
            }
            
            utils.setSessionData(sessionData);
            
            // If we have enough customer data, try to associate
            if (sessionData.customWidgetData.email) {
                this.associateCustomer(sessionData.customWidgetData);
            }
        },
        
        trackCustomerDataInput: function() {
            /**
             * Track customer data as it's being filled out in multi-step forms
             */
            let customerData = {};
            let associationTimeout = null;
            
            utils.log('Setting up customer data input tracking');
            
            // Track input changes on any field that looks like customer data
            document.addEventListener('input', (e) => {
                const field = e.target;
                const fieldName = field.name || field.id || field.placeholder || '';
                const fieldValue = field.value;
                
                // Debug: Log all input changes
                if (fieldValue && fieldValue.length > 0) {
                    utils.log('Input field detected:', { fieldName, fieldValue, type: field.type, tagName: field.tagName });
                }
                
                // Check if this is a customer data field
                if (this.isCustomerDataField(field, fieldName)) {
                    utils.log('Customer data field changed:', fieldName, fieldValue);
                    
                    // Update customer data object
                    if (fieldName.toLowerCase().includes('first') || fieldName.toLowerCase().includes('given')) {
                        customerData.first_name = fieldValue;
                    } else if (fieldName.toLowerCase().includes('last') || fieldName.toLowerCase().includes('family') || fieldName.toLowerCase().includes('surname')) {
                        customerData.last_name = fieldValue;
                    } else if (fieldName.toLowerCase().includes('email') || field.type === 'email') {
                        customerData.email = fieldValue;
                    } else if (fieldName.toLowerCase().includes('phone') || field.type === 'tel') {
                        customerData.phone_number = fieldValue;
                    }
                    
                    // Clear previous timeout and set new one
                    if (associationTimeout) {
                        clearTimeout(associationTimeout);
                    }
                    
                    // Associate customer after a delay (to avoid too many API calls)
                    associationTimeout = setTimeout(() => {
                        if (this.hasValidCustomerData(customerData)) {
                            utils.log('Associating customer with collected data:', customerData);
                            this.associateCustomer(customerData).catch(error => {
                                utils.error('Customer association failed', error);
                            });
                        } else {
                            utils.log('Not enough customer data yet:', customerData);
                        }
                    }, 2000); // Wait 2 seconds after last input
                }
            });
        },
        
        trackFormSubmissions: function() {
            /**
             * Track form submissions as backup
             */
            document.addEventListener('submit', (e) => {
                const form = e.target;
                utils.log('Form submitted:', form);
                
                // Collect all customer data from the form
                const customerData = this.collectFormData(form);
                utils.log('Collected form data on submit:', customerData);
                
                if (this.hasValidCustomerData(customerData)) {
                    utils.log('Form submission with customer data:', customerData);
                    setTimeout(() => {
                        this.associateCustomer(customerData).catch(error => {
                            utils.error('Customer association failed', error);
                        });
                    }, 1000);
                }
            });
        },
        
        isCustomerDataField: function(field, fieldName) {
            /**
             * Check if a field is likely to contain customer data
             */
            const name = fieldName.toLowerCase();
            const type = field.type.toLowerCase();
            
            return (
                type === 'email' ||
                type === 'tel' ||
                name.includes('first') || name.includes('given') ||
                name.includes('last') || name.includes('family') || name.includes('surname') ||
                name.includes('email') ||
                name.includes('phone') || name.includes('mobile')
            );
        },
        
        collectFormData: function(form) {
            /**
             * Collect customer data from a form
             */
            const customerData = {};
            
            // Look for customer data fields
            const emailField = form.querySelector('input[type="email"], input[name*="email"], input[placeholder*="email" i]');
            const firstNameField = form.querySelector('input[name*="first"], input[placeholder*="first" i], input[id*="first"]');
            const lastNameField = form.querySelector('input[name*="last"], input[placeholder*="last" i], input[id*="last"]');
            const phoneField = form.querySelector('input[name*="phone"], input[type="tel"], input[placeholder*="phone" i], input[id*="phone"]');
            
            if (emailField) customerData.email = emailField.value;
            if (firstNameField) customerData.first_name = firstNameField.value;
            if (lastNameField) customerData.last_name = lastNameField.value;
            if (phoneField) customerData.phone_number = phoneField.value;
            
            return customerData;
        },
        
        hasValidCustomerData: function(customerData) {
            /**
             * Check if we have enough customer data to associate
             */
            return customerData.email || customerData.phone_number || 
                   (customerData.first_name && customerData.last_name);
        },
        
        detectAccountForms: function() {
            /**
             * Detect account forms on page load and set up monitoring
             */
            const forms = document.querySelectorAll('form');
            utils.log(`Found ${forms.length} forms on page`);
            
            forms.forEach((form, index) => {
                const hasEmailField = form.querySelector('input[type="email"], input[name*="email"], input[placeholder*="email" i]');
                const hasAccountFields = form.querySelector('input[name*="first"], input[name*="last"], input[name*="phone"], input[type="tel"]');
                
                if (hasEmailField || hasAccountFields) {
                    utils.log(`Form ${index} appears to be an account form`);
                    
                    // Monitor form for changes
                    form.addEventListener('input', (e) => {
                        if (e.target.matches('input[type="email"], input[name*="email"], input[placeholder*="email" i], input[name*="first"], input[name*="last"], input[name*="phone"], input[type="tel"]')) {
                            utils.log('Account form field changed:', e.target.name || e.target.type, e.target.value);
                        }
                    });
                }
            });
        }
    };
    
    // Shopify-specific integrations
    const ShopifyIntegration = {
        
        init: function() {
            // Wait for Shopify to load
            if (window.Shopify) {
                this.setupShopifyHooks();
            } else {
                // Wait for Shopify to load
                document.addEventListener('shopify:section:load', this.setupShopifyHooks.bind(this));
            }
        },
        
        setupShopifyHooks: function() {
            utils.log('Setting up Shopify hooks');
            
            // Hook into cart events
            if (window.Shopify && window.Shopify.theme) {
                // Override cart drawer/form submissions
                this.overrideCartSubmission();
                this.overrideCheckoutFlow();
            }
        },
        
        overrideCartSubmission: function() {
            // Override cart form submissions to include UTM data
            const forms = document.querySelectorAll('form[action*="/cart"]');
            forms.forEach(form => {
                form.addEventListener('submit', function(e) {
                    const sessionData = utils.getSessionData();
                    
                    if (sessionData.utmParams) {
                        // Add hidden inputs for UTM parameters
                        Object.entries(sessionData.utmParams).forEach(([key, value]) => {
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = key;
                            input.value = value;
                            form.appendChild(input);
                        });
                        
                        // Add session tracking data
                        const sessionInput = document.createElement('input');
                        sessionInput.type = 'hidden';
                        sessionInput.name = 'steer_session_data';
                        sessionInput.value = JSON.stringify(sessionData);
                        form.appendChild(sessionInput);
                        
                        utils.log('Added UTM params to cart form');
                    }
                });
            });
        },
        
        overrideCheckoutFlow: function() {
            // Override checkout button clicks
            document.addEventListener('click', function(e) {
                if (e.target.matches('[href*="/checkout"]') || 
                    e.target.closest('[href*="/checkout"]')) {
                    
                    const sessionData = utils.getSessionData();
                    if (sessionData.utmParams) {
                        // Store UTM params in cookie for checkout page
                        utils.setCookie('steer_checkout_utm', JSON.stringify(sessionData.utmParams), 1);
                        utils.log('Stored UTM params for checkout');
                    }
                }
            });
        }
    };
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            SteerTracking.init();
            SteerTracking.setupCustomerAssociation();
            ShopifyIntegration.init();
            
            // Auto-inject UTM params into forms after a short delay
            setTimeout(() => {
                window.injectSteerUtmParams();
            }, 1000);
        });
    } else {
        SteerTracking.init();
        SteerTracking.setupCustomerAssociation();
        ShopifyIntegration.init();
        
        // Auto-inject UTM params into forms after a short delay
        setTimeout(() => {
            window.injectSteerUtmParams();
        }, 1000);
    }
    
    // Expose to global scope for debugging
    window.SteerTracking = SteerTracking;
    window.getSteerSessionId = function() { return utils.ensureSessionId(); };
    
    // Add manual trigger for testing
    window.triggerCustomerAssociation = function(email, firstName, lastName, phone) {
        SteerTracking.associateCustomer({
            email: email || 'test@example.com',
            first_name: firstName || 'Test',
            last_name: lastName || 'User',
            phone_number: phone || '555-123-4567'
        });
    };
    
    // Add function to test form detection
    window.testFormDetection = function() {
        const forms = document.querySelectorAll('form');
        console.log(`Found ${forms.length} forms on page`);
        forms.forEach((form, index) => {
            console.log(`Form ${index}:`, {
                action: form.action,
                method: form.method,
                className: form.className,
                id: form.id,
                hasEmail: !!form.querySelector('input[type="email"], input[name*="email"]'),
                hasFirstName: !!form.querySelector('input[name*="first"]'),
                hasLastName: !!form.querySelector('input[name*="last"]'),
                hasPhone: !!form.querySelector('input[name*="phone"], input[type="tel"]')
            });
        });
    };
    
    // Add function to get UTM parameters for booking forms
    window.getSteerUtmParams = function() {
        // First try to get from cookies (for cross-domain access)
        const cookieData = utils.getCookie('steer_utm_params');
        if (cookieData) {
            try {
                const utmParams = JSON.parse(cookieData);
                utils.log('Retrieved UTM params from cookie:', utmParams);
                return utmParams;
            } catch (e) {
                utils.error('Failed to parse UTM params from cookie', e);
            }
        }
        
        // Fallback to session storage
        const sessionData = utils.getSessionData();
        if (sessionData.utmParams) {
            utils.log('Retrieved UTM params from session storage:', sessionData.utmParams);
            return sessionData.utmParams;
        }
        
        // Fallback to current URL params
        const urlParams = utils.getUrlParams();
        if (Object.keys(urlParams).length > 0) {
            utils.log('Retrieved UTM params from URL:', urlParams);
            return urlParams;
        }
        
        utils.log('No UTM parameters found');
        return {};
    };
    
    // Add function to inject UTM params into any form
    window.injectSteerUtmParams = function(formSelector = 'form') {
        const utmParams = window.getSteerUtmParams();
        if (Object.keys(utmParams).length === 0) {
            utils.log('No UTM parameters to inject');
            return false;
        }
        
        const forms = document.querySelectorAll(formSelector);
        let injectedCount = 0;
        
        forms.forEach(form => {
            // Check if UTM params are already injected
            const existingUtmInputs = form.querySelectorAll('input[name^="utm_"]');
            if (existingUtmInputs.length > 0) {
                utils.log('UTM params already present in form, skipping');
                return;
            }
            
            // Inject UTM parameters as hidden inputs
            Object.entries(utmParams).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                form.appendChild(input);
                injectedCount++;
            });
            
            // Also add session tracking data
            const sessionData = utils.getSessionData();
            if (sessionData && Object.keys(sessionData).length > 0) {
                const sessionInput = document.createElement('input');
                sessionInput.type = 'hidden';
                sessionInput.name = 'steer_session_data';
                sessionInput.value = JSON.stringify(sessionData);
                form.appendChild(sessionInput);
                injectedCount++;
            }
            
            utils.log(`Injected ${injectedCount} tracking parameters into form`);
        });
        
        return injectedCount > 0;
    };
    
})();
